import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
  Spin,
  Alert,
  Tag,
  Row,
  Col,
  Tooltip,
} from "antd";
import BranchForm from "./BranchForm";
import ProductForm from "../products/ProductForm";
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import CompanyFormModal from "./CompanyFormModal";
import BranchFormModal from "./BranchForm";
import ProductFormModal from "../products/ProductForm";

const { TextArea } = Input;
const { Title } = Typography;

const CompanyMasterWithBranches = () => {
  const [companies, setCompanies] = useState([]);
  const [offers, setOffers] = useState([]);
  const [showOffersModal, setShowOffersModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [showBranchForm, setShowBranchForm] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const successTimeoutRef = React.useRef(null);
  const [companyForm] = Form.useForm();
  const [expandedVariantKeys, setExpandedVariantKeys] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Add state to control expanded rows
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/company-master`, {
        headers: { Authorization: token },
      });
      setCompanies(response.data);
    } catch (err) {
      setError("Failed to fetch companies");
      message.error("Failed to fetch companies");
    } finally {
      setLoading(false);
    }
  };

  // ... (keep your existing handlers for company/branch)
  const handleCompanySubmit = async (values) => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      const token = localStorage.getItem("token");
      if (selectedCompany) {
        values.agent_id = selectedCompany.agent_id;
        await axios.put(
          `${API_BASE_URL}/company-master/${selectedCompany.id}`,
          values,
          { headers: { Authorization: token } }
        );
        setSuccess("Company updated successfully");
        message.success("Company updated successfully");
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(() => setSuccess(""), 3000);
      } else {
        await axios.post(`${API_BASE_URL}/company-master`, values, {
          headers: { Authorization: token },
        });
        setSuccess("Company created successfully");
        message.success("Company created successfully");
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(() => setSuccess(""), 3000);
      }
      setShowCompanyForm(false);
      companyForm.resetFields();
      setSelectedCompany(null);
      fetchCompanies();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save company");
      message.error(err.response?.data?.message || "Failed to save company");
    } finally {
      setLoading(false);
    }
  };
  const handleCompanyEdit = (company) => {
    setSelectedCompany(company);
    companyForm.setFieldsValue(company);
    setShowCompanyForm(true);
  };

  const handleCompanyDelete = async (companyId) => {
    Modal.confirm({
      title:
        "Are you sure you want to delete this company and all its branches, products, and offers?",
      onOk: async () => {
        try {
          setLoading(true);
          const token = localStorage.getItem("token");
          await axios.delete(`${API_BASE_URL}/company-master/${companyId}`, {
            headers: { Authorization: token },
          });
          setSuccess("Company deleted successfully");
          message.success("Company deleted successfully");
          if (successTimeoutRef.current)
            clearTimeout(successTimeoutRef.current);
          successTimeoutRef.current = setTimeout(() => setSuccess(""), 3000);
          fetchCompanies();
        } catch (err) {
          setError("Failed to delete company");
          message.error("Failed to delete company");
        } finally {
          setLoading(false);
        }
      },
    });
  };
  const handleBranchSubmit = async (branchData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (editingBranch) {
        await axios.put(
          `${API_BASE_URL}/branches/company/${selectedCompany.id}/${editingBranch.id}`,
          branchData,
          { headers: { Authorization: token } }
        );
        setSuccess("Branch updated successfully");
        message.success("Branch updated successfully");
      } else {
        await axios.post(
          `${API_BASE_URL}/branches/company/${selectedCompany.id}`,
          branchData,
          { headers: { Authorization: token } }
        );
        setSuccess("Branch created successfully");
        message.success("Branch created successfully");
      }
      setShowBranchForm(false);
      setEditingBranch(null);
      fetchCompanies();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save branch");
      message.error(err.response?.data?.message || "Failed to save branch");
    } finally {
      setLoading(false);
    }
  };
  const handleBranchEdit = (branch) => {
    setEditingBranch(branch);
    setSelectedCompany(companies.find((c) => c.id === branch.company_id));
    setShowBranchForm(true);
  };

  const handleProductSubmit = async (values) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const url = editingProduct
        ? `${API_BASE_URL}/products/company/${selectedCompany.id}/${editingProduct.id}`
        : `${API_BASE_URL}/products/company/${selectedCompany.id}`;
      const method = editingProduct ? "put" : "post";
      await axios[method](url, values, {
        headers: { Authorization: token },
      });
      setSuccess(
        `Product ${editingProduct ? "updated" : "added"} successfully`
      );
      message.success(
        `Product ${editingProduct ? "updated" : "added"} successfully`
      );
      setShowProductForm(false);
      setEditingProduct(null);
      fetchCompanies();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save product");
      message.error(err.response?.data?.message || "Failed to save product");
    } finally {
      setLoading(false);
    }
  };

  const handleProductEdit = (product) => {
    setEditingProduct(product);
    setShowProductForm(true);
  };

  const handleProductDelete = async (companyId, productId) => {
    Modal.confirm({
      title: "Are you sure you want to delete this product and all its offers?",
      onOk: async () => {
        try {
          setLoading(true);
          const token = localStorage.getItem("token");
          await axios.delete(
            `${API_BASE_URL}/products/company/${companyId}/${productId}`,
            { headers: { Authorization: token } }
          );
          setSuccess("Product deleted successfully");
          message.success("Product deleted successfully");
          fetchCompanies();
        } catch (err) {
          setError("Failed to delete product");
          message.error("Failed to delete product");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleProductOffer = async (product) => {
    try {
      setLoading(true);
      setSelectedProduct(product);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/offers?product_id=${product.id}`,
        {
          headers: { Authorization: token },
        }
      );
      setOffers(response.data.data);
      setShowOffersModal(true);
    } catch (err) {
      setError("Failed to fetch offers");
      message.error("Failed to fetch offers");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const isOfferActive = (offer) => {
    return offer.is_active;
  };

  const calculateDiscountPercentage = (original, discounted) => {
    const originalPrice = parseFloat(original);
    const discountedPrice = parseFloat(discounted);
    if (originalPrice <= 0) return 0;
    return Math.round(
      ((originalPrice - discountedPrice) / originalPrice) * 100
    );
  };

  const handleBranchDelete = async (companyId, branchId) => {
    Modal.confirm({
      title: "Are you sure you want to delete this branch?",
      onOk: async () => {
        try {
          setLoading(true);
          const token = localStorage.getItem("token");
          await axios.delete(
            `${API_BASE_URL}/branches/company/${companyId}/${branchId}`,
            { headers: { Authorization: token } }
          );
          setSuccess("Branch deleted successfully");
          message.success("Branch deleted successfully");
          if (successTimeoutRef.current)
            clearTimeout(successTimeoutRef.current);
          successTimeoutRef.current = setTimeout(() => setSuccess(""), 3000);
          fetchCompanies();
        } catch (err) {
          setError("Failed to delete branch");
          message.error("Failed to delete branch");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // const companyColumns = [
  //   {
  //     title: "Company Name",
  //     dataIndex: "company_name",
  //     key: "company_name",
  //     width: 200,
  //   },
  //   {
  //     title: "Business Type",
  //     dataIndex: "business_type",
  //     key: "business_type",
  //     width: 150,
  //   },
  //   {
  //     title: "Phone",
  //     dataIndex: "phone",
  //     key: "phone",
  //     width: 150,
  //   },
  //   {
  //     title: "Email",
  //     dataIndex: "email",
  //     key: "email",
  //     width: 200,
  //   },
  //   {
  //     title: "Actions",
  //     align: "center",
  //     key: "actions",
  //     width: 200,
  //     render: (_, record) => (
  //       <Space>
  //         <Button
  //           type="text"
  //           icon={<EditOutlined style={{ color: "green" }} />}
  //           onClick={() => handleCompanyEdit(record)}
  //           style={{ border: "none" }}
  //         />
  //         <Button
  //           type="text"
  //           icon={<DeleteOutlined style={{ color: "red" }} />}
  //           onClick={() => handleCompanyDelete(record.id)}
  //           style={{ border: "none" }}
  //         />
  //         <Tooltip title="Add Branch">
  //           <Button
  //             type="primary"
  //             icon={<PlusOutlined />}
  //             onClick={() => {
  //               setSelectedCompany(record);
  //               setEditingBranch(null);
  //               setShowBranchForm(true);
  //             }}
  //             style={{
  //               background: "#1677ff",
  //               color: "white",
  //               border: "none",
  //               marginLeft: 4,
  //             }}
  //           >
  //             Branch
  //           </Button>
  //         </Tooltip>
  //         <Tooltip title="Add Product">
  //           <Button
  //             type="primary"
  //             icon={<PlusOutlined />}
  //             onClick={() => {
  //               setSelectedCompany(record);
  //               setEditingProduct(null);
  //               setShowProductForm(true);
  //             }}
  //             style={{
  //               background: "#1677ff",
  //               color: "white",
  //               border: "none",
  //               marginLeft: 4,
  //             }}
  //           >
  //             Product
  //           </Button>
  //         </Tooltip>
  //       </Space>
  //     ),
  //   },
  // ];

  // const branchColumns = [
  //   {
  //     title: "Branch Name",
  //     dataIndex: "branch_name",
  //     key: "branch_name",
  //     width: 200,
  //     render: (text, record) => (
  //       <div>
  //         {text}
  //         {record.is_head_office && (
  //           <Tag color="blue" style={{ marginLeft: 8 }}>
  //             Head Office
  //           </Tag>
  //         )}
  //       </div>
  //     ),
  //   },
  //   {
  //     title: "City",
  //     dataIndex: "city",
  //     key: "city",
  //     width: 150,
  //   },
  //   {
  //     title: "Phone",
  //     dataIndex: "phone",
  //     key: "phone",
  //     width: 150,
  //   },
  //   {
  //     title: "Pincode",
  //     dataIndex: "pincode",
  //     key: "pincode",
  //     width: 100,
  //   },
  //   {
  //     title: "Actions",
  //     key: "actions",
  //     align: "center",
  //     width: 100,
  //     render: (_, branch) => (
  //       <Space>
  //         <Button
  //           type="text"
  //           icon={<EditOutlined style={{ color: "green" }} />}
  //           onClick={() => handleBranchEdit(branch)}
  //           style={{ border: "none" }}
  //         />
  //         <Button
  //           type="text"
  //           icon={<DeleteOutlined style={{ color: "red" }} />}
  //           onClick={() => handleBranchDelete(branch.company_id, branch.id)}
  //           style={{ border: "none" }}
  //         />
  //       </Space>
  //     ),
  //   },
  // ];

  // const productColumns = [
  //   {
  //     title: "Name",
  //     dataIndex: "name",
  //     key: "name",
  //     width: 200,
  //   },
  //   {
  //     title: "Model",
  //     dataIndex: "model",
  //     key: "model",
  //     width: 150,
  //   },
  //   {
  //     title: "Price",
  //     dataIndex: "price",
  //     key: "price",
  //     width: 100,
  //     render: (price) => `₹${parseFloat(price).toFixed(2)}`,
  //   },
  //   {
  //     title: "Short Description",
  //     dataIndex: "short_description",
  //     key: "short_description",
  //     width: 200,
  //   },
  //   {
  //     title: "Actions",
  //     align: "center",
  //     key: "actions",
  //     width: 150,
  //     render: (_, product) => (
  //       <Space>
  //         <Button
  //           type="text"
  //           icon={<EditOutlined style={{ color: "green" }} />}
  //           onClick={() => {
  //             if (product) {
  //               setSelectedCompany(companies.find((c) => c.id === product.company_id) || null);
  //               handleProductEdit(product);
  //             }
  //           }}
  //           style={{ border: "none" }}
  //         />
  //         <Button
  //           type="text"
  //           icon={<DeleteOutlined style={{ color: "red" }} />}
  //           onClick={() => handleProductDelete(product.company_id, product.id)}
  //           style={{ border: "none" }}
  //         />
  //         <Button
  //           type="text"
  //           icon={<EyeOutlined style={{ color: "blue" }} />}
  //           onClick={() => handleProductOffer(product)}
  //           style={{ border: "none" }}
  //         />
  //       </Space>
  //     ),
  //   },
  // ];

  // // Define columns for product variants
  // const variantColumns = [
  //   {
  //     title: "Variant Name",
  //     dataIndex: "name",
  //     key: "name",
  //   },
  //   {
  //     title: "Price",
  //     dataIndex: "price",
  //     key: "price",
  //     render: (price) => `₹${parseFloat(price).toFixed(2)}`,
  //   },
  // ];

  // const expandedProductRowRender = (product) => {
  //   return (
  //     <Table
  //       columns={variantColumns}
  //       dataSource={product.variants || []}
  //       rowKey="id"
  //       pagination={false}
  //     />
  //   );
  // };

  // const expandedRowRender = (company) => {
  //   return (
  //     <div style={{ padding: "0 24px" }}>
  //       <Title
  //         level={4}
  //         style={{
  //           background: "#E6F4FF",
  //           color: "#1677ff",
  //           fontWeight: 500,
  //           fontSize: "18px",
  //           padding: "12px 16px",
  //           borderRadius: "8px",
  //           marginBottom: "16px",
  //           border: "1px solid #e5e7eb",
  //         }}
  //       >
  //         Branches
  //       </Title>

  //       <Table
  //         className="hidden-scroll"
  //         scroll={{ y: 400, x: "100%" }}
  //         columns={branchColumns}
  //         dataSource={company.CompanyBranches}
  //         rowKey="id"
  //         pagination={false}
  //       />

  //       <Title
  //         style={{
  //           background: "#E6F4FF",
  //           color: "#1677ff",
  //           fontWeight: 500,
  //           fontSize: "18px",
  //           padding: "12px 16px",
  //           borderRadius: "8px",
  //           marginBottom: "16px",
  //           border: "1px solid #e5e7eb",
  //           marginTop: 16,
  //         }}
  //         level={5}
  //       >
  //         Products
  //       </Title>
  //       <Table
  //         className="hidden-scroll"
  //         scroll={{ y: 400, x: "100%" }}
  //         columns={productColumns}
  //         dataSource={company.Products}
  //         rowKey="id"
  //         expandable={{
  //           expandedRowRender: expandedProductRowRender,
  //           defaultExpandedRowKeys: [],
  //         }}
  //         pagination={false}
  //       />
  //     </div>
  //   );
  // };

  return (
    <div className="p-4">
      <Row
        justify="space-between"
        align="middle"
        gutter={[16, 16]}
        style={{ marginBottom: "20px" }}
      >
        <Col xs={24} sm={24} md={12} lg={12}>
          <Title level={2}>Company Master Management</Title>
        </Col>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setSelectedCompany(null);
            companyForm.resetFields();
            setShowCompanyForm(true);
          }}
        >
          Add New Company
        </Button>
      </Row>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {error && <Alert message={error} type="error" showIcon />}
        {/* {success && <Alert message={success} type="success" showIcon />} */}
        {loading ? (
          <Spin size="large" tip="Loading..." />
        ) : (
          // <Table
          //   className="hidden-scroll"
          //   scroll={{ y: 600, x: "100%" }}
          //   columns={companyColumns}
          //   dataSource={companies}
          //   rowKey="id"
          //   expandable={{
          //     expandedRowRender,
          //     expandedRowKeys: expandedRowKeys,
          //     onExpand: (expanded, record) => {
          //       if (expanded) {
          //         setExpandedRowKeys([...expandedRowKeys, record.id]);
          //       } else {
          //         setExpandedRowKeys(expandedRowKeys.filter((id) => id !== record.id));
          //       }
          //     },
          //   }}
          //   pagination={{
          //     pageSize: 10,
          //     showSizeChanger: true,
          //     position: ["bottomRight"],
          //   }}
          // />
          <div className="rounded-2 table-responsive">
            <table
              className="table align-middle mb-0 shadow-sm"
              style={{ tableLayout: "auto" }}
            >
              <thead>
                <tr style={{ background: "#f5f5f5" }}>
                  <th>Company Name</th>
                  <th>Business Type</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th className="text-center" style={{ width: "250px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <React.Fragment key={company.id}>
                    {/* Company Row */}
                    <tr
                      // onClick={() => {
                      //   if (expandedRowKeys.includes(company.id)) {
                      //     setExpandedRowKeys(
                      //       expandedRowKeys.filter((id) => id !== company.id)
                      //     );
                      //   } else {
                      //     setExpandedRowKeys([...expandedRowKeys, company.id]);
                      //   }
                      // }}
                      // className="expandable-row"
                    >
                      <td>
                        {company.company_name}
                        <span
                          className={`expand-arrow ${
                            expandedRowKeys.includes(company.id)
                              ? "expanded"
                              : ""
                          } `}
                        >
                          {/* ▼ */}
                        </span>
                      </td>
                      <td>{company.business_type}</td>
                      <td>{company.phone}</td>
                      <td>{company.email}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <Space>
                          <Button
                            type="text"
                            icon={<EditOutlined style={{ color: "green" }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCompanyEdit(company);
                            }}
                          />
                          <Button
                            type="text"
                            icon={<DeleteOutlined style={{ color: "red" }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCompanyDelete(company.id);
                            }}
                          />
                          {/* <Tooltip title="Add Branch">
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCompany(company);
                                setEditingBranch(null);
                                setShowBranchForm(true);
                              }}
                            >
                              Branch
                            </Button>
                          </Tooltip>
                          <Tooltip title="Add Product">
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCompany(company);
                                setEditingProduct(null);
                                setShowProductForm(true);
                              }}
                            >
                              Product
                            </Button>
                          </Tooltip> */}
                        </Space>
                      </td>
                    </tr>

                    {/* Expanded Company Details */}
                    {expandedRowKeys.includes(company.id) && (
                      <tr>
                        <td colSpan={5}>
                          <div className="p-3 bg-light rounded-2">
                            {/* Branches Section */}
                            <Title level={5}>Branches</Title>
                            {company.CompanyBranches?.length > 0 ? (
                              <div className="rounded-2 overflow-hidden mb-4">
                                <table
                                  className="table align-middle mb-0 shadow-sm"
                                  style={{ tableLayout: "auto" }}
                                >
                                  <thead className="table-light">
                                    <tr>
                                      <th>Branch Name</th>
                                      <th>City</th>
                                      <th>Phone</th>
                                      <th>Pincode</th>
                                      <th className="text-center">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {company.CompanyBranches.map((branch) => (
                                      <tr key={branch.id}>
                                        <td>
                                          {branch.branch_name}{" "}
                                          {branch.is_head_office && (
                                            <Tag color="blue">Head Office</Tag>
                                          )}
                                        </td>
                                        <td>{branch.city}</td>
                                        <td>{branch.phone}</td>
                                        <td>{branch.pincode}</td>
                                        <td
                                          style={{ whiteSpace: "nowrap" }}
                                          className="text-center"
                                        >
                                          <Button
                                            type="text"
                                            icon={
                                              <EditOutlined
                                                style={{ color: "green" }}
                                              />
                                            }
                                            onClick={() =>
                                              handleBranchEdit(branch)
                                            }
                                          />
                                          <Button
                                            type="text"
                                            icon={
                                              <DeleteOutlined
                                                style={{ color: "red" }}
                                              />
                                            }
                                            onClick={() =>
                                              handleBranchDelete(
                                                branch.company_id,
                                                branch.id
                                              )
                                            }
                                          />
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p>No branches found.</p>
                            )}

                            {/* Products Section */}
                            <Title level={5}>Products</Title>
                            {company.Products?.length > 0 ? (
                              <table
                                className="table align-middle mb-0 shadow-sm"
                                style={{ tableLayout: "auto" }}
                              >
                                <thead className="table-light">
                                  <tr>
                                    <th>Product Name</th>
                                    <th>Model</th>
                                    <th>Price</th>
                                    <th>Short Description</th>
                                    <th className="text-center">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {company.Products.map((product) => (
                                    <React.Fragment key={product.id}>
                                      <tr
                                        onClick={() => {
                                          if (
                                            expandedVariantKeys.includes(
                                              product.id
                                            )
                                          ) {
                                            setExpandedVariantKeys(
                                              expandedVariantKeys.filter(
                                                (id) => id !== product.id
                                              )
                                            );
                                          } else {
                                            setExpandedVariantKeys([
                                              ...expandedVariantKeys,
                                              product.id,
                                            ]);
                                          }
                                        }}
                                        className={
                                          product.variants?.length > 0
                                            ? "expandable-row"
                                            : ""
                                        }
                                      >
                                        <td>
                                          {product.name}
                                          {product.variants?.length > 0 && (
                                            <span
                                              className={`expand-arrow ${
                                                expandedVariantKeys.includes(
                                                  product.id
                                                )
                                                  ? "expanded"
                                                  : ""
                                              }`}
                                            >
                                              ▼
                                            </span>
                                          )}
                                        </td>
                                        <td>{product.model}</td>
                                        <td>
                                          ₹
                                          {parseFloat(product.price).toFixed(2)}
                                        </td>
                                        <td>{product.short_description}</td>
                                        <td
                                          style={{ whiteSpace: "nowrap" }}
                                          className="text-center"
                                        >
                                          <Space>
                                            <Button
                                              type="text"
                                              icon={
                                                <EditOutlined
                                                  style={{ color: "green" }}
                                                />
                                              }
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedCompany(company);
                                                handleProductEdit(product);
                                              }}
                                            />
                                            <Button
                                              type="text"
                                              icon={
                                                <DeleteOutlined
                                                  style={{ color: "red" }}
                                                />
                                              }
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleProductDelete(
                                                  company.id,
                                                  product.id
                                                );
                                              }}
                                            />
                                            <Button
                                              type="text"
                                              icon={
                                                <EyeOutlined
                                                  style={{ color: "blue" }}
                                                />
                                              }
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleProductOffer(product);
                                              }}
                                            />
                                          </Space>
                                        </td>
                                      </tr>

                                      {/* Product Variants */}
                                      {product.variants?.length > 0 &&
                                        expandedVariantKeys.includes(
                                          product.id
                                        ) && (
                                          <tr>
                                            <td colSpan={5}>
                                              <div className="p-2 ms-4">
                                                <Title
                                                  level={5}
                                                  style={{ marginBottom: 8 }}
                                                >
                                                  Variants for {product.name}
                                                </Title>
                                                <div className="rounded-2 overflow-hidden">
                                                  <table className="table mb-0">
                                                    <thead className="table-light">
                                                      <tr>
                                                        <th>Variant Name</th>
                                                        <th>Price</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {product.variants.map(
                                                        (variant) => (
                                                          <tr key={variant.id}>
                                                            <td>
                                                              {variant.name}
                                                            </td>
                                                            <td>
                                                              ₹
                                                              {parseFloat(
                                                                variant.price
                                                              ).toFixed(2)}
                                                            </td>
                                                          </tr>
                                                        )
                                                      )}
                                                    </tbody>
                                                  </table>
                                                </div>
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p>No products found.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {companies.length === 0 && !loading && (
          <Alert
            message="No companies found. Add your first company to get started."
            type="info"
            showIcon
          />
        )}
      </Space>

      {/* Company Form Modal */}
      {/* <Modal
        title={selectedCompany ? "Edit Company" : "Add New Company"}
        open={showCompanyForm}
        onCancel={() => {
          setShowCompanyForm(false);
          setSelectedCompany(null);
        }}
        footer={null}
        width={800}
      >
        <Form
          form={companyForm}
          onFinish={handleCompanySubmit}
          layout="vertical"
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="company_name"
                label="Company Name"
                rules={[
                  { required: true, message: "Company name is required" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="business_type" label="Business Type">
                <Select placeholder="Select Type">
                  <Select.Option value="Private Limited">
                    Private Limited
                  </Select.Option>
                  <Select.Option value="Public Limited">
                    Public Limited
                  </Select.Option>
                  <Select.Option value="Partnership">Partnership</Select.Option>
                  <Select.Option value="Sole Proprietorship">
                    Sole Proprietorship
                  </Select.Option>
                  <Select.Option value="LLP">LLP</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="business_description" label="Business Description">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="business_brief" label="Business Brief">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="caller_name" label="Caller Name">
            <TextArea rows={2} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[{ required: true, message: "Phone is required" }]}
              >
                <Input type="tel" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ required: true, message: "Email is required" }]}
              >
                <Input type="email" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="website" label="Website">
                <Input type="url" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="established_year" label="Established Year">
                <Input
                  type="number"
                  min={1900}
                  max={new Date().getFullYear()}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="gst_number" label="GST Number">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="pan_number" label="PAN Number">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="registration_number" label="Registration Number">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <div style={{ textAlign: "end" }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  setShowCompanyForm(false);
                  setSelectedCompany(null);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {loading ? "Saving..." : "Save Company"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal> */}
      <CompanyFormModal
        showCompanyForm={showCompanyForm}
        setShowCompanyForm={setShowCompanyForm}
        selectedCompany={selectedCompany}
        setSelectedCompany={setSelectedCompany}
        handleCompanySubmit={handleCompanySubmit}
        loading={loading}
      />

      {/* Branch Form Modal */}
      {/* {showBranchForm && selectedCompany && (
        <BranchForm
          companyId={selectedCompany.id}
          branch={editingBranch}
          onSubmit={handleBranchSubmit}
          onCancel={() => {
            setShowBranchForm(false);
            setEditingBranch(null);
          }}
          loading={loading}
        />
      )} */}

      {showBranchForm && selectedCompany && (
        <BranchFormModal
          showBranchForm={showBranchForm}
          setShowBranchForm={setShowBranchForm}
          selectedCompany={selectedCompany}
          branch={editingBranch}
          setEditingBranch={setEditingBranch}
          handleBranchSubmit={handleBranchSubmit}
          loading={loading}
        />
      )}

      {/* Product Form Modal */}
      {/* {showProductForm && selectedCompany && (
        <Modal
          title={editingProduct ? "Edit Product" : "Add Product"}
          open={showProductForm}
          onCancel={() => {
            setShowProductForm(false);
            setEditingProduct(null);
          }}
          footer={null}
        >
          <ProductForm
            companyId={selectedCompany.id}
            product={editingProduct}
            onSubmit={handleProductSubmit}
            onCancel={() => {
              setShowProductForm(false);
              setEditingProduct(null);
            }}
            loading={loading}
          />
        </Modal>
      )} */}

      {showProductForm && selectedCompany && (
        <ProductFormModal
          showProductForm={showProductForm}
          setShowProductForm={setShowProductForm}
          selectedCompany={selectedCompany}
          product={editingProduct}
          setEditingProduct={setEditingProduct}
          handleProductSubmit={handleProductSubmit}
          loading={loading}
        />
      )}

      <Modal
        title={`Offers for ${selectedProduct?.name || ""}`}
        open={showOffersModal}
        onCancel={() => setShowOffersModal(false)}
        footer={null}
        width={800}
      >
        {offers.length === 0 ? (
          <Alert
            message="No offers found for this product."
            type="info"
            showIcon
          />
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {offers.map((offer) => (
              <div
                key={offer.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  padding: 16,
                  flex: "1 1 300px",
                  background: isOfferActive(offer) ? "#f0f9eb" : "#fef0f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h4 style={{ margin: 0 }}>{offer.title}</h4>
                  <Tag color={isOfferActive(offer) ? "green" : "red"}>
                    {isOfferActive(offer) ? "Active" : "Inactive"}
                  </Tag>
                </div>
                <p style={{ margin: "8px 0" }}>{offer.description}</p>
                <div style={{ display: "flex", gap: 8, margin: "8px 0" }}>
                  <span
                    style={{ textDecoration: "line-through", color: "#999" }}
                  >
                    ${offer.original_price}
                  </span>
                  <span style={{ fontWeight: "bold", color: "#f5222d" }}>
                    ${offer.discounted_price}
                  </span>
                  <span style={{ color: "#f5222d" }}>
                    (
                    {calculateDiscountPercentage(
                      offer.original_price,
                      offer.discounted_price
                    )}
                    % OFF)
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "#666" }}>
                  <p>
                    <strong>Product:</strong> {offer.Product?.name || "N/A"}
                  </p>
                  <p>
                    <strong>Valid:</strong> {formatDate(offer.start_date)} -{" "}
                    {formatDate(offer.end_date)}
                  </p>
                  {offer.max_uses && (
                    <p>
                      <strong>Max Uses:</strong> {offer.max_uses}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CompanyMasterWithBranches;
